import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Callout } from '@/shared/ui/Callout';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { errorMessage } from '@/modules/reviews/sms/hooks';
import {
  textingAdminApi,
  type AdminNumberCheck,
  type AdminRegistrationCheck,
  type AdminTextingView,
  type RegistrationState,
} from '../../api/textingAdminApi';
import type { Location } from '../../api/agencyApi';
import { agencyKeys } from '../../queryKeys';
import { reviewKeys } from '@/modules/reviews/queryKeys';
import styles from './TextingAdminPanel.module.css';

/** "+19255550188" → "(925) 555-0188". */
function prettyPhone(e164: string | null | undefined): string {
  const d = (e164 ?? '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (e164 ?? '');
}

const STATE_LABEL: Record<RegistrationState, string> = {
  not_submitted: 'Not filed',
  pending: 'Pending',
  vetting: 'In vetting',
  approved: 'Approved',
  rejected: 'Rejected',
  suspended: 'Suspended',
};

function StatePill({ state }: { state: string }) {
  const tone =
    state === 'approved'
      ? styles.pillOk
      : state === 'rejected' || state === 'suspended'
        ? styles.pillBad
        : state === 'not_submitted'
          ? styles.pillTodo
          : styles.pillWait;
  return <span className={tone}>{STATE_LABEL[state as RegistrationState] ?? state}</span>;
}

/**
 * Telnyx shows two IDs per brand and campaign. We need Telnyx's own (a long ID with dashes, the
 * "ID" / "Campaign ID" column); the short TCR ID (e.g. BU2O4W7, CH3F6R5) is The Campaign
 * Registry's and Telnyx's API does not find records by it.
 */
function looksLikeTcrId(value: string): boolean {
  return /^[A-Z0-9]{5,9}$/i.test(value.trim());
}
const TCR_ID_ERROR =
  'That looks like the short TCR ID. Copy the long ID (with dashes) from the column next to it in Telnyx.';

function overall(view: AdminTextingView): { label: string; tone: string } {
  if (view.number.provisioningState !== 'active')
    return { label: 'Waiting on texting number', tone: styles.pillWait };
  if (!view.registration.approved)
    return { label: 'Waiting on carrier registration', tone: styles.pillWait };
  return { label: 'Ready to text', tone: styles.pillOk };
}

/**
 * Command Center → a practice → Settings → Texting (agency admins only).
 *
 * The practice requests a number and fills in carrier registration on its own Texting page;
 * PracticeNova sets both up by hand on Telnyx and a check every 3 hours notices. Here an admin who
 * has finished on Telnyx connects the number or links the brand and campaign straight away. Both
 * are checked on Telnyx first. The practice sees the result on its own Texting page.
 */
export function TextingAdminPanel({
  practiceId,
  practiceName,
  location,
}: {
  practiceId: string;
  practiceName: string;
  location: Location;
}) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: agencyKeys.texting(practiceId, location.id),
    queryFn: () => textingAdminApi.get(practiceId, location.id),
  });

  if (isLoading) return <Skeleton height={240} borderRadius="var(--card-radius)" />;
  if (isError || !data)
    return (
      <Callout variant="error" title="Couldn’t load texting setup">
        {errorMessage(error)}
      </Callout>
    );

  const status = overall(data);
  return (
    <Card
      title={`Texting · ${location.name}`}
      subtitle="The number patients get texts from and its carrier registration, shared by every feature that texts (today: Reviews → Text after the visit). The practice sees the same steps on its own Texting page. Use this when you've set something up on Telnyx and don't want to wait for the 3-hourly check."
      action={<span className={status.tone}>{status.label}</span>}
    >
      <div className={styles.stack}>
        {/* Keyed by location: a switch must not carry one location's inputs into another's. */}
        <NumberStep
          key={`n-${location.id}`}
          practiceId={practiceId}
          location={location}
          view={data}
        />
        <RegistrationStep
          key={`r-${location.id}`}
          practiceId={practiceId}
          practiceName={practiceName}
          location={location}
          view={data}
        />
        <p className={styles.hint}>
          Every change here is recorded in the Audit Log. The practice sees the new status on its
          Texting page; nobody is emailed, so tell them if they are waiting on it.
        </p>
      </div>
    </Card>
  );
}

function useSaveView(practiceId: string, locationId: string) {
  const qc = useQueryClient();
  return (view: AdminTextingView) => {
    qc.setQueryData(agencyKeys.texting(practiceId, locationId), view);
    void qc.invalidateQueries({ queryKey: agencyKeys.reviewSmsStatus(practiceId, locationId) });
    void qc.invalidateQueries({ queryKey: reviewKeys.smsProgram(locationId) });
  };
}

function NumberStep({
  practiceId,
  location,
  view,
}: {
  practiceId: string;
  location: Location;
  view: AdminTextingView;
}) {
  const toast = useToast();
  const save = useSaveView(practiceId, location.id);
  const n = view.number;
  const [input, setInput] = useState(view.request?.e164 ?? '');
  const [note, setNote] = useState('');
  const [check, setCheck] = useState<AdminNumberCheck | null>(null);
  // Where calls go: what the request said, else the location phone (as the backend does).
  const forwardTo = view.request?.forwardTo ?? location.phone ?? null;

  const checkMut = useMutation({
    mutationFn: () => textingAdminApi.checkNumber(practiceId, location.id, input),
    onSuccess: (next: AdminNumberCheck) => {
      setCheck(next);
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const connectMut = useMutation({
    mutationFn: () =>
      textingAdminApi.connectNumber(practiceId, location.id, input, {
        note: note || undefined,
      }),
    onSuccess: (next) => {
      save(next);
      setCheck(null);
      toast.success('Number connected. The practice sees it on their Texting page.');
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  if (n.provisioningState === 'active') {
    return (
      <section className={styles.step}>
        <header className={styles.stepHeader}>
          <h3 className={styles.stepTitle}>1 · Texting number</h3>
          <span className={styles.pillOk}>Live</span>
        </header>
        <dl className={styles.dl}>
          <div className={styles.row}>
            <dt>Number</dt>
            <dd className={styles.mono}>{n.masked}</dd>
          </div>
          <div className={styles.row}>
            <dt>Set up</dt>
            <dd>
              {n.setupByPracticeNova
                ? 'By PracticeNova from the Command Center'
                : 'Found by the 3-hourly check'}
              {n.requestedLast4 ? ` · they asked for the number ending ${n.requestedLast4}` : ''}
            </dd>
          </div>
        </dl>
      </section>
    );
  }

  const ready =
    !!check &&
    check.onAccount &&
    check.active &&
    !check.usedByOtherLocation &&
    !check.alreadyLive &&
    check.e164.replace(/\D/g, '').slice(-10) === input.replace(/\D/g, '').slice(-10);

  return (
    <section className={styles.step}>
      <header className={styles.stepHeader}>
        <h3 className={styles.stepTitle}>1 · Texting number</h3>
        <span className={view.request ? styles.pillWait : styles.pillTodo}>
          {view.request ? 'Requested' : 'Not requested'}
        </span>
      </header>
      <p className={styles.body}>
        {view.request ? (
          <>
            The practice asked for{' '}
            <span className={styles.mono}>{prettyPhone(view.request.e164)}</span> (
            {view.request.mode === 'practice_hosted'
              ? 'their own number, hosted'
              : 'a new local number'}
            )
            {view.request.forwardTo && (
              <>
                , calls forwarding to{' '}
                <span className={styles.mono}>{prettyPhone(view.request.forwardTo)}</span>
              </>
            )}
            . Enter the number you bought or connected on Telnyx; it can differ from the request.
          </>
        ) : (
          'The practice hasn’t asked for a number yet. You can still connect one you set up on Telnyx.'
        )}
      </p>
      <div className={styles.inline}>
        <Input
          label="Number on Telnyx"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setCheck(null);
          }}
          placeholder="+1 925 555 0188"
        />
        <Button
          variant="secondary"
          size="sm"
          isLoading={checkMut.isPending}
          disabled={input.replace(/\D/g, '').length < 10}
          onClick={() => checkMut.mutate()}
        >
          Check on Telnyx
        </Button>
      </div>

      {check && !check.onAccount && (
        <Callout variant="error" title="Not on the PracticeNova Telnyx account">
          Buy it first (Telnyx → Numbers) or check for a typo. Nothing was changed.
        </Callout>
      )}
      {check && check.onAccount && !check.active && (
        <Callout
          variant="warning"
          title={`On the account, not active yet (${check.telnyxStatus ?? 'unknown'})`}
        >
          Connect it once Telnyx shows it as active. The 3-hourly check will also pick it up then.
        </Callout>
      )}
      {check && check.usedByOtherLocation && (
        <Callout variant="error" title="Another location already texts from this number">
          A number belongs to one location. Nothing was changed.
        </Callout>
      )}
      {ready && check && (
        <Callout variant="success" title="On the Telnyx account and ready to connect">
          <div className={styles.stack}>
            <span>
              Connecting routes replies to Betty
              {forwardTo ? `, forwards calls to ${prettyPhone(forwardTo)}` : ''}, marks it live and
              opens carrier registration.
            </span>
            {check.otherProfileId && (
              <span className={styles.hint}>
                It’s on another messaging profile on Telnyx ({check.otherProfileId}); connecting
                moves it to this location.
              </span>
            )}
            {check.differsFromRequestLast4 && (
              <span className={styles.warnText}>
                Not the number they asked for (ending {check.differsFromRequestLast4}). Let them
                know which one they got.
              </span>
            )}
            <Input
              label="Note for the audit log (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 775 was taken; bought 925 instead"
            />
            <div className={styles.actions}>
              <Button
                variant="primary"
                size="sm"
                isLoading={connectMut.isPending}
                onClick={() => connectMut.mutate()}
              >
                Connect and mark live
              </Button>
            </div>
          </div>
        </Callout>
      )}
    </section>
  );
}

function RegistrationStep({
  practiceId,
  practiceName,
  location,
  view,
}: {
  practiceId: string;
  practiceName: string;
  location: Location;
  view: AdminTextingView;
}) {
  const toast = useToast();
  const save = useSaveView(practiceId, location.id);
  const reg = view.registration;
  const [brandId, setBrandId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [check, setCheck] = useState<AdminRegistrationCheck | null>(null);

  const reset = () => {
    setCheck(null);
    setConfirm(false);
  };
  const checkMut = useMutation({
    mutationFn: () =>
      textingAdminApi.checkRegistration(practiceId, location.id, brandId.trim(), campaignId.trim()),
    onSuccess: setCheck,
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const linkMut = useMutation({
    mutationFn: () =>
      textingAdminApi.linkRegistration(practiceId, location.id, {
        brandId: brandId.trim(),
        campaignId: campaignId.trim(),
        ...(check?.einMatch === null ? { confirmBrand: confirm } : {}),
        ...(note ? { note } : {}),
      }),
    onSuccess: (next) => {
      save(next);
      reset();
      toast.success(
        next.registration.approved
          ? 'Linked and approved. Texting is open; the practice sees it on their Texting page.'
          : 'Linked. We’ll follow it to approval every 3 hours.',
      );
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });
  const recheckMut = useMutation({
    mutationFn: () => textingAdminApi.recheckRegistration(practiceId, location.id),
    onSuccess: (next) => {
      save(next);
      toast.success(
        next.registration.approved
          ? 'Approved on Telnyx. Texting is open; the practice sees it on their Texting page.'
          : 'Checked Telnyx: still pending with carriers.',
      );
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const linked =
    reg.submittedAt &&
    (reg.brandState !== 'not_submitted' || reg.campaignState !== 'not_submitted');
  const canLink =
    !!check &&
    check.campaign.belongsToBrand &&
    check.einMatch !== false &&
    (check.einMatch === true || confirm);

  return (
    <section className={styles.step}>
      <header className={styles.stepHeader}>
        <h3 className={styles.stepTitle}>2 · Carrier registration (10DLC)</h3>
        {reg.approved ? (
          <span className={styles.pillOk}>Approved</span>
        ) : !view.registrationOpen ? (
          <span className={styles.pillTodo}>Waiting on number</span>
        ) : linked ? (
          <span className={styles.pillWait}>Pending with carriers</span>
        ) : (
          <span className={styles.pillTodo}>Not linked</span>
        )}
      </header>

      {!view.registrationOpen && <p className={styles.body}>Opens once step 1 is live.</p>}

      {view.registrationOpen && linked && (
        <dl className={styles.dl}>
          <div className={styles.row}>
            <dt>Brand</dt>
            <dd>
              <StatePill state={reg.brandState} />
            </dd>
          </div>
          <div className={styles.row}>
            <dt>Campaign</dt>
            <dd>
              <StatePill state={reg.campaignState} />
            </dd>
          </div>
          {reg.rejectionReason && (
            <div className={styles.row}>
              <dt>Reason</dt>
              <dd>{reg.rejectionReason}</dd>
            </div>
          )}
          <div className={styles.row}>
            <dt>Applies to</dt>
            <dd>Every location of {practiceName}</dd>
          </div>
          <div className={styles.row}>
            <dt>Last checked</dt>
            <dd>
              {reg.lastCheckedAt ? new Date(reg.lastCheckedAt).toLocaleString() : 'Not yet'}
              {!reg.approved && (
                <Button
                  variant="primary"
                  size="sm"
                  isLoading={recheckMut.isPending}
                  onClick={() => recheckMut.mutate()}
                >
                  Check now
                </Button>
              )}
            </dd>
          </div>
        </dl>
      )}

      {view.registrationOpen && !reg.approved && (
        <>
          <p className={styles.body}>
            {linked
              ? 'To link a different filing (e.g. one re-filed after a rejection), paste its IDs.'
              : 'Paste the IDs from Telnyx (Messaging → 10DLC). We read both and check the brand belongs to this practice.'}
            {view.practiceEin ? (
              <>
                {' '}
                EIN on file: <span className={styles.mono}>{view.practiceEin}</span>.
              </>
            ) : (
              ' The practice hasn’t given an EIN yet, so you’ll confirm the brand yourself.'
            )}
          </p>
          <div className={styles.grid2}>
            <Input
              label="Brand ID"
              value={brandId}
              onChange={(e) => {
                setBrandId(e.target.value);
                reset();
              }}
              placeholder="4b2001a0-efb3-be2c-…"
              hint="Telnyx → Messaging Suite → 10DLC → 10DLC Brands: the “ID” column (long, with dashes). Not the TCR ID."
              error={looksLikeTcrId(brandId) ? TCR_ID_ERROR : undefined}
            />
            <Input
              label="Campaign ID"
              value={campaignId}
              onChange={(e) => {
                setCampaignId(e.target.value);
                reset();
              }}
              placeholder="4b3001a0-f4f0-cdfe-…"
              hint="Telnyx → Messaging Suite → 10DLC → 10DLC Campaigns: the “Campaign ID” column (long, with dashes). Not the TCR ID."
              error={looksLikeTcrId(campaignId) ? TCR_ID_ERROR : undefined}
            />
          </div>
          <div className={styles.actionsStart}>
            <Button
              variant="secondary"
              size="sm"
              isLoading={checkMut.isPending}
              disabled={
                brandId.trim().length < 3 ||
                campaignId.trim().length < 3 ||
                looksLikeTcrId(brandId) ||
                looksLikeTcrId(campaignId)
              }
              onClick={() => checkMut.mutate()}
            >
              Check on Telnyx
            </Button>
          </div>

          {check && (
            <div className={styles.result}>
              <dl className={styles.dl}>
                <div className={styles.row}>
                  <dt>Brand</dt>
                  <dd>
                    {check.brand.companyName ?? check.brand.brandId}
                    {check.brand.ein ? ` · EIN ${check.brand.ein}` : ''}{' '}
                    <StatePill state={check.brand.state} />
                  </dd>
                </div>
                <div className={styles.row}>
                  <dt>Campaign</dt>
                  <dd>
                    {check.campaign.usecase
                      ? `${check.campaign.usecase.replace(/_/g, ' ').toLowerCase()} · `
                      : ''}
                    <StatePill state={check.campaign.state} />
                  </dd>
                </div>
              </dl>
              {!check.campaign.belongsToBrand && (
                <Callout variant="error" title="That campaign belongs to a different brand">
                  Check the IDs. Nothing was changed.
                </Callout>
              )}
              {check.einMatch === false && (
                <Callout variant="error" title="This brand’s EIN isn’t this practice’s">
                  Linking it would text patients under another company’s registration. Nothing was
                  changed.
                </Callout>
              )}
              {check.einMatch === true && (
                <span className={styles.okText}>The brand’s EIN matches the practice’s.</span>
              )}
              {check.sharedWithPractices > 0 && (
                <span className={styles.warnText}>
                  This campaign is already linked to {check.sharedWithPractices} other practice
                  {check.sharedWithPractices === 1 ? '' : 's'}. Fine for PracticeNova’s own testing;
                  a customer should have its own.
                </span>
              )}
              {check.einMatch === null && check.campaign.belongsToBrand && (
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={confirm}
                    onChange={(e) => setConfirm(e.target.checked)}
                  />
                  <span>
                    This brand is {practiceName}’s (or PracticeNova’s own, for testing). We can’t
                    compare EINs because none is on file.
                  </span>
                </label>
              )}
              {canLink && (
                <>
                  <Input
                    label="Note for the audit log (optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <p className={styles.body}>
                    {check.brand.state === 'approved' && check.campaign.state === 'approved'
                      ? 'Both are approved: linking attaches the texting number to the campaign and opens texting right away, tests included.'
                      : 'Linking attaches the number once approved and checks every 3 hours. Texting opens when both are approved.'}
                  </p>
                  <div className={styles.actions}>
                    <Button
                      variant="primary"
                      size="sm"
                      isLoading={linkMut.isPending}
                      onClick={() => linkMut.mutate()}
                    >
                      {check.brand.state === 'approved' && check.campaign.state === 'approved'
                        ? 'Link and open texting'
                        : 'Link and follow approval'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
